/**
 * LLM tool definition for `generate_formula` — resolves a natural-language
 * query to a FactSet =FDS() formula via the TTF API. Returns a rich response
 * including the formula, a reusable template, ticker, inputs, description, and
 * validation status so the agent can apply the pattern across multiple cells
 * without re-calling.
 */

import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import {
  resolveFormulaViaTtfRich,
  resolveFormulasViaTtfRichBatch,
} from "./services/factset/ttfCellDispatch";
import {
  FACTSET_TTF_ERROR_CODES,
  FACTSET_TTF_QUERY_MAX_BYTES,
  TTF_MAX_CONCURRENT_QUERIES,
} from "../shared/factset/factsetConstants";
import { diag } from "./aiDiag";
import { FQL_DATE_INPUT_GUIDANCE } from "./fqlDatePolicy";

/** Per-tool-call timeout for deterministic formula resolution. */
export const TOOL_CALL_TIMEOUT_MS = 5 * 60_000;
export const FORMULA_QUERIES_REQUIRED_ERROR_CODE = "factset_ttf_queries_required";

export interface GenerateFormulaToolOptions {
  requireQueries?: boolean;
}

export interface GenerateFormulaExecutionOptions {
  requireQueries?: boolean;
}

interface RecoverableFormulaQueryError {
  error: string;
  code: typeof FACTSET_TTF_ERROR_CODES.QUERY_TOO_LARGE;
  retryable: true;
  retryWith: "queries";
  queryBytes: number;
  maxQueryBytes: number;
}

interface RecoverableFormulaQueriesRequiredError {
  error: string;
  code: typeof FORMULA_QUERIES_REQUIRED_ERROR_CODE;
  retryable: true;
  retryWith: "queries";
  minQueries: 1;
}

function getFormulaQueriesRequiredError(): RecoverableFormulaQueriesRequiredError {
  return {
    error:
      "This generate_formula call requires a non-empty `queries` array " +
      "with exactly one concise metric per entry. Do not use the singular `query` field. " +
      "Retry generate_formula exactly once with corrected `queries`.",
    code: FORMULA_QUERIES_REQUIRED_ERROR_CODE,
    retryable: true,
    retryWith: "queries",
    minQueries: 1,
  };
}

function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit <= 0x7f) {
      bytes += 1;
    } else if (codeUnit <= 0x7ff) {
      bytes += 2;
    } else if (
      codeUnit >= 0xd800 &&
      codeUnit <= 0xdbff &&
      index + 1 < value.length
    ) {
      const nextCodeUnit = value.charCodeAt(index + 1);
      if (nextCodeUnit >= 0xdc00 && nextCodeUnit <= 0xdfff) {
        bytes += 4;
        index += 1;
      } else {
        // An unpaired surrogate is encoded as the three-byte replacement character.
        bytes += 3;
      }
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

function getFormulaQuerySizeError(query: string): RecoverableFormulaQueryError | null {
  const queryBytes = utf8ByteLength(query.trim());
  if (queryBytes <= FACTSET_TTF_QUERY_MAX_BYTES) return null;

  return {
    error:
      `FactSet TTF query is ${queryBytes} UTF-8 bytes; the maximum is ` +
      `${FACTSET_TTF_QUERY_MAX_BYTES}. Retry generate_formula exactly once with ` +
      "`queries`: one concise, non-repeating entry per metric.",
    code: FACTSET_TTF_ERROR_CODES.QUERY_TOO_LARGE,
    retryable: true,
    retryWith: "queries",
    queryBytes,
    maxQueryBytes: FACTSET_TTF_QUERY_MAX_BYTES,
  };
}

function getFormulaQueryValidationError(
  query: string,
): RecoverableFormulaQueryError | RecoverableFormulaQueriesRequiredError | null {
  if (!query.trim()) return getFormulaQueriesRequiredError();
  return getFormulaQuerySizeError(query);
}

/**
 * @returns Today's local date in the MM/DD/YYYY format required when an
 *   absolute date is included in an outbound TTF query.
 */
function todayTtfDate(): string {
  const now = new Date();
  const local = `${String(now.getMonth() + 1).padStart(2, "0")}/${String(now.getDate()).padStart(2, "0")}/${now.getFullYear()}`;
  diag("date", "today_computed", { local, tzOffsetMin: now.getTimezoneOffset(), source: "formulaTools" });
  return local;
}

/**
 * Build the `generate_formula` tool definition with today's date embedded.
 *
 * @param options - Set `requireQueries` for executor scopes, where every call
 *   must use the batch-shaped contract even when only one metric is needed.
 * @returns A `ChartCapabilityToolDef` describing the tool's name, description,
 *   and JSON schema for the LLM tool-calling interface.
 */
export function buildGenerateFormulaTool(
  options: GenerateFormulaToolOptions = {},
): ChartCapabilityToolDef {
  const requireQueries = options.requireQueries === true;
  const executorInputContract = requireQueries
    ? `EXECUTOR INPUT CONTRACT:\n` +
      `- Always pass a non-empty "queries" array. This is required even when you need only one metric; use a one-item array.\n` +
      `- The singular "query" field is not accepted in executor runs.\n` +
      `- Every array entry must request exactly one concise metric. Multiple metrics require multiple entries.\n\n`
    : "";
  const queriesSchema = {
    type: "array",
    minItems: 1,
    maxItems: TTF_MAX_CONCURRENT_QUERIES,
    items: {
      type: "string",
      minLength: 1,
      maxLength: FACTSET_TTF_QUERY_MAX_BYTES,
      description:
        "One concise natural-language query containing company ID, exactly one metric, and time period.",
    },
    description:
      `Non-empty batch of concise natural-language queries (up to ${TTF_MAX_CONCURRENT_QUERIES}, maximum ${FACTSET_TTF_QUERY_MAX_BYTES} UTF-8 bytes each) to resolve together in ONE call — exactly one metric per entry.`,
  };
  return {
    name: "generate_formula",
    description:
      `Generates FactSet Excel formula(s) from natural-language query(ies) via the TTF API.\n\n` +
      `Today's date: ${todayTtfDate()}. Use this to determine correct period offsets. ` +
      `When a query includes a numeric absolute date, write it as MM/DD/YYYY.\n\n` +
      executorInputContract +
      `BATCHING (IMPORTANT FOR SPEED):\n` +
      `- To resolve several DIFFERENT metrics at once, pass "queries" (array of up to ${TTF_MAX_CONCURRENT_QUERIES} strings) in a SINGLE call instead of calling this tool repeatedly. Batched queries are resolved concurrently, so N metrics cost roughly one round trip instead of N.\n` +
      `- The batch response is { "results": [ { "query", ...formula fields } | { "query", "error" }, ... ] }, aligned in order with your input queries; one failed query does not fail the others.\n` +
      `- Each query string requests exactly ONE metric (e.g. revenue). Never list multiple metrics in one query string (NOT "AAPL-US revenue, EBITDA and net income"). To fetch several metrics, put each metric in its own "queries" entry.\n` +
      `- Keep every query below ${FACTSET_TTF_QUERY_MAX_BYTES} UTF-8 bytes. Never stack synonyms, repeat clauses, or concatenate alternate phrasings in an attempt to improve matching.\n` +
      `- Create one entry per unique METRIC (not one per cell/ticker/period).${requireQueries ? "" : ' Use "query" (single string) only when you need exactly one metric.'}\n` +
      `- PLAN FOR REUSE BEFORE QUERYING: Inspect the target ranges and group required cells into formula families by metric and dimensional shape. Query once for each unique metric/template family, then reuse the returned formula_template by binding its ticker, period, date, currency and unit placeholders to the appropriate worksheet cells. Do not issue separate queries merely because the same metric appears for different companies, periods or workbook locations.\n` +
      `- Good batched shape: generate_formula({ queries: ["AAPL-US revenue for the most recent quarter in USD", "AAPL-US EBITDA for the most recent quarter in USD"] }).\n\n` +
      `RESPONSE FIELDS (${requireQueries ? 'per entry in "results"' : 'single-query mode, or per entry in "results"'}):\n` +
      `- formula: the ready-to-use =FDS(...) formula with all values inlined. It can be omitted when only formula_template is date-policy safe.\n` +
      `- formula_template: the same formula with placeholder tokens (e.g. {A1}, {A2}) so you can substitute other tickers/periods without re-calling.\n` +
      `- placeholders: map of each placeholder name to its current value.\n` +
      `- ticker: the resolved FactSet identifier.\n` +
      `- description / commentary: explains what the formula returns.\n` +
      `- formatHint: structured output-scale semantics and a recommended Excel number format. percentage_points means the raw result already represents percentage points and must use a literal percent sign; decimal_fraction may use Excel's multiplying percent format.\n` +
      `- dataUnavailable: true means FactSet carries no such data for that company/metric/period. This is final, not a transient failure: never resubmit the query. Write the literal text n/a into the target cells, note it, and treat the step as done.\n\n` +
      `USAGE PATTERN:\n` +
      `- Create one query entry per unique metric (not one per cell, ticker, or period), and batch independent entries in one call.\n` +
      `- Preserve the exact formula/value. When formatHint.semantics is "percentage_points", use formatHint.recommendedNumberFormat (for example 0.00"%"); never use 0.00%, multiply/divide by 100, or infer scale from value magnitude. When semantics is "decimal_fraction", Excel's 0.00% format is valid. Unknown hints do not authorize percent scaling.\n` +
      `- For chat/standalone answers, you may adapt formula or formula_template textually (substituting placeholders in the string). For workbook/edit flows, do NOT substitute directly into a formula string — bind every placeholder to visible cells instead (see WORKBOOK TEMPLATE BINDING below).\n` +
      `- Only re-call this tool when the metric changes, or once when your review of the returned formula and metadata shows that it does not match the user's requested dimensions or granularity. Never re-call merely to fetch another ticker or period that can be represented by the returned template.\n` +
      `- SERVER-SIDE CACHE: FactSet caches TTF results on their side. When you need the SAME data point again, send the exact SAME query text (same wording, FactSet identifier form, period phrasing, casing, and units) so FactSet can return its cached result. Do not paraphrase or reorder a query you have already issued unless the response explicitly has retryable=true.\n` +
      `- TIME-SERIES: If the user requests multiple periods (e.g. "last 8 quarters", "daily prices for 20 days"), ask for a time series in ONE concise query: name the metric once, name the frequency, state the window as explicitly labelled tokens ("Start Date -9D and End Date 0D" — not "for the last 10 trading days through today", which TTF often returns with the endpoints reversed), and ask for one observation per period. Keep it short — stacked synonyms, duplicated words, and repeated clauses measurably reduce match quality, so a longer query is not a stronger query. Do NOT reduce the request to a single period. If the correct result genuinely is a single-period template, use formula_template + placeholders to construct the series by varying the period placeholder (e.g. 0Q, -1Q, -2Q, ...).\n` +
      `- _FRQ SERIES ITEMS: Whenever the request is obviously per-period — a daily, weekly, or monthly series, an OHLC table, "last N days" — and the metric has a _FRQ twin, ask TTF for the _FRQ item BY NAME. P_PRICE_HIGH and P_PRICE_LOW are single-VALUE items: P_PRICE_HIGH(-14D,0D,D) returns ONE number, the highest price reached anywhere in the window, not a high for each day. A frequency argument, "INTRA", or "PRICE" does not change that, so a valid (start,end,frequency) argument list proves nothing about the shape. P_PRICE_OPEN and P_PRICE (close) already return one observation per period and have no _FRQ form, so in an OHLC table only High and Low need P_PRICE_HIGH_FRQ and P_PRICE_LOW_FRQ.\n` +
      `- _FRQ QUERY WORDING: Word a _FRQ query as: identifier, the _FRQ mnemonic, the metric in plain words, "time series", "with Start Date <earlier> and End Date <later>", then "one observation per trading day". Example: "NFLX-US P_PRICE_HIGH_FRQ daily intraday high price time series with Start Date -14D and End Date 0D, one observation per trading day". TTF sometimes returns the plain twin anyway, even for a correctly worded query. Do NOT resend the identical query text: FactSet caches by exact query text, so a verbatim resend returns the same wrong item and wastes a call. Retry at most once with genuinely different wording that still names the _FRQ item — for example lead with "frequency-aware daily high price series".\n` +
      `- _FRQ CORRECTION: Allowed only after that reworded retry also comes back without the suffix. Rebuild the formula around the _FRQ item as <ITEM>_FRQ(<start>,<end>,<frequency>), keeping the identifier, window, and frequency you asked for — for example =FDS($C$3,"P_PRICE_HIGH_FRQ("&$C$4&","&$C$5&",D)"). REBUILD it; do not string-rename the mnemonic in place. The twins take different argument lists and the plain item's trailing single-value arguments do not carry over: TTF returns shapes like P_PRICE_LOW(-14D,0D,,,,"PRICE","INTRA") with no frequency argument at all, so a rename would leave the frequency empty. Drop "PRICE", "INTRA", and any other trailing arguments the plain item carried. A corrected formula is NOT TTF-validated, so refresh and recalculate again and confirm the column fills one row per requested period; if it still does not resolve, remove it and report the gap rather than leaving a broken or single-value column in a series table.\n` +
      `- SERIES SHAPE VERIFICATION: Refresh FactSet data and recalculate BEFORE judging any series. A cell reading #Calc or #VALUE has no result yet, so never mark a formula step successful while one is present. After recalculation, a spilling series must fill one row per requested period. If a column fills only its FIRST row while a sibling series over the same identifier, window, and frequency fills every row, that column's item collapsed the window into a single value: re-ask generate_formula for that one metric using the _FRQ retry wording above — reworded, never the identical query text — then replace only that formula. If TTF still returns the plain twin, apply the _FRQ CORRECTION above and re-verify. Never hardcode or fabricate the missing periods, and never leave a single-value item standing in a per-period series column.\n` +
      `- TIME-SERIES DATE AXIS: When a table needs a Date column, use the companion shape =FDSC("-", identifier, "julian(<the exact returned value series>.dates)"), preserving its identifier, window, and frequency. A Date header with no date-axis formula is incomplete. This returns true Excel serial dates: format the entire spill as dd-mmm-yyyy by default (for example, 10-Jul-2026), unless the user explicitly requests another date format. Do not use JULIAN(P_DATE(...)), P_DATE(), SEQUENCE, WORKDAY, or calendar arithmetic for the displayed date axis; do not otherwise alter the returned series expression.\n` +
      `- WORKBOOK TEMPLATE BINDING (edit/workbook flows only): When formula_template + placeholders are returned, formula_template is the source of truth — do NOT write the inlined formula directly. Bind EVERY placeholder to a visible worksheet cell. Handle Date/As of Date/Start Date/End Date placeholders before generic literals. Resolve absolute dates to numeric Excel cells with =DATE(year,month,day), apply a date number format, concatenate the raw cell reference, and never write an absolute date as text. Preserve returned relative FQL tokens such as -9D and 0D as text, in chronological Start-to-End order (for example, -9D through 0D, never 0 through -9D). generate_formula safely restores an exactly reversed TTF pair when the query explicitly states the ordered start and end; if it still returns a reversed-range error, retry once with clearly labelled start and end dates rather than stopping after the first recoverable error. If date semantics are ambiguous, resolve them from the request/context or create clearly labelled editable inputs. For non-date concrete literals (AAPL-US, 0Q, -1AY, USD, M), write the value in the input cell. Never write a raw semantic label as a cell value. Reconstruct the final formula by substituting the returned placeholder tokens with cell references and "&" concatenation exactly within the returned formula_template; do not invent or alter the FactSet data-item text. Use absolute refs for shared inputs ($B$5) and row-relative refs for per-row inputs ($C23). The inlined formula is only for chat/standalone answers or when formula_template is absent.\n` +
      `${FQL_DATE_INPUT_GUIDANCE}\n` +
      `- SPILL LAYOUT: FDS formulas that return time-series/daily arrays will spill down many rows. Place each spilling formula at the top of a blank column with enough empty rows below. Do not place independent spilling formulas in adjacent cells unless you intentionally reserve non-overlapping spill ranges.\n` +
      `- GUARDRAIL: Every financial data formula must originate from generate_formula. If formula_template is present, write the workbook formula through placeholder bindings. If formula_template is absent, use only the exact returned formula — do not edit, extend, or recreate it from memory. Exactly two transformations are approved: (1) the matching time-series Date companion described above, wrapping the exact returned value-series expression as julian(<series>.dates), and (2) the _FRQ CORRECTION above, permitted only for a _FRQ item you explicitly named in the query, only after the reworded retry also failed, and only once the column has demonstrably collapsed to a single value after a refresh and recalculation. Outside those two cases, never rename a returned data item and do NOT create or modify FDS/FDSC/FDSZ formulas from memory in later calculation, verification, or remediation steps.\n\n` +
      `- RESULT FIT: Use the returned formula, template, description, commentary, and placeholders together to judge whether the result matches the user's requested metric, dimensions, granularity, period, frequency, series shape, currency, units, and explicit listing. For multi-period requests, confirm that the returned item is frequency-aware and is intended to produce one observation per requested period. Upstream validation confirms formula validity, not complete semantic fit. If the result is materially different, retry once with a concise clarification that preserves the original intent; do not silently substitute a different dataset.\n\n` +
      `DO NOT USE FOR QUALITATIVE RESEARCH:\n` +
      `- Do NOT use this tool for SEC filings, earnings transcripts, management commentary, business strategy, competitor narratives, industry research, IR documents, or any qualitative document research.\n` +
      `- For those requests, use the research tool (\`finster_query\` / \`factset_ai_query\`) and cite its returned answer instead.\n\n` +
      `QUERY REQUIREMENTS:\n` +
      `1. Company identifier matching any explicitly requested exchange, country, or currency listing (e.g. "AAPL-US", "MSFT-US", "005930-KR")\n` +
      `2. Financial metric (e.g. "revenue", "diluted EPS", "free cash flow")\n` +
      `3. Specific time period (e.g. "for the most recent quarter", "for 2 quarters ago", "for fiscal year 2024")\n\n` +
      `GOOD: "AAPL-US revenue for the most recent quarter"\n` +
      `GOOD: "MSFT-US diluted EPS for 2 quarters ago"\n` +
      `BAD:  "Apple revenue" (missing identifier format, missing period)\n` +
      `BAD:  "AAPL-US financials" (too vague)\n\n` +
      `ON ERROR: If the response has retryable=true and retryWith="queries", retry exactly once with concise one-metric queries while preserving the user's requested semantics. For any other error, do not retry or write an FDS formula from your own knowledge; report the failure or skip that cell. Your internal FDS syntax knowledge is unreliable.`,
    // JSON Schema maxLength counts characters, not encoded bytes. It is an
    // advisory upper bound; getFormulaQuerySizeError is authoritative.
    inputSchema: requireQueries
      ? {
          type: "object",
          properties: { queries: queriesSchema },
          required: ["queries"],
          additionalProperties: false,
        }
      : {
          type: "object",
          properties: {
            query: {
              type: "string",
              minLength: 1,
              maxLength: FACTSET_TTF_QUERY_MAX_BYTES,
              description:
                `Single concise natural-language query containing company ID, exactly one metric, and time period; maximum ${FACTSET_TTF_QUERY_MAX_BYTES} UTF-8 bytes. Provide this OR \`queries\`.`,
            },
            queries: {
              ...queriesSchema,
              description:
                `${queriesSchema.description} Preferred when a step needs several FactSet metrics. Provide this OR \`query\`.`,
            },
          },
          additionalProperties: false,
        },
  };
}

export const GENERATE_FORMULA_TOOL: ChartCapabilityToolDef =
  buildGenerateFormulaTool();

/**
 * Returns a fresh tool definition with today's date so long-running sessions
 * never serve a stale date to the LLM.
 *
 * @returns A new `ChartCapabilityToolDef` with the current date in the description.
 */
export function getGenerateFormulaToolFresh(
  options: GenerateFormulaToolOptions = {},
): ChartCapabilityToolDef {
  return buildGenerateFormulaTool(options);
}

/**
 * Execute the `generate_formula` tool call and return both the model-facing JSON
 * envelope and optional Langfuse observation metadata. Resolves the query via the
 * FactSet TTF API and returns a rich JSON response including:
 *   - formula: the inlined ready-to-use =FDS(...) formula, when policy-safe
 *   - formula_template: reusable template with placeholder tokens (e.g. {A1}, {A2})
 *   - placeholders: map of each token to its current literal value
 *   - ticker: the resolved FactSet identifier
 *   - inputs: named parameter values (e.g. { Period: "0Q" })
 *   - description: human-readable description of what the formula returns
 *   - validated: upstream validation status when FactSet returns it
 *
 * On failure the envelope is `{ error }`. Invalid executor input shape and
 * oversize model-generated queries carry structured one-retry guidance; all
 * other failures must not fall back to the LLM's own FDS syntax knowledge.
 *
 * The upstream FactSet TTF `chainId` rides ONLY on `observationMetadata`
 * (single mode -> `factsetChainId`, batch mode -> `factsetChainIds`) and is
 * stripped from the model-facing `output`, mirroring the PowerPoint tool path
 * so the trace correlates without changing agent behavior.
 *
 * @param rawInput - The tool call arguments object from the LLM. Expected shape:
 *   `{ query: string }` (or `{ queries: string[] }` for the batch path).
 * @param signal - Optional `AbortSignal` to cancel the underlying TTF HTTP request
 *   if the user aborts the agent step.
 * @param options - Executor-only input contract options.
 * @returns `{ output, observationMetadata? }` where `output` is the serialized
 *   JSON string (rich result / `{ results }` / `{ error }`).
 */
export async function executeGenerateFormulaToolDetailed(
  rawInput: unknown,
  signal?: AbortSignal,
  options: GenerateFormulaExecutionOptions = {},
): Promise<{ output: string; observationMetadata?: Record<string, unknown> }> {
  const input = rawInput as Record<string, unknown> | null;

  // Batch mode: `queries` (array) resolves several metrics concurrently in one
  // tool call. Bounded, order-preserving, deduped, and semaphore-throttled by
  // `resolveFormulasViaTtfRichBatch`.
  const queriesRaw = input?.queries;
  if (options.requireQueries && (!Array.isArray(queriesRaw) || queriesRaw.length === 0)) {
    return { output: JSON.stringify(getFormulaQueriesRequiredError()) };
  }
  if (Array.isArray(queriesRaw) && queriesRaw.length > 0) {
    const queries = queriesRaw.map((q) =>
      (typeof q === "string" ? q : String(q ?? "")).trim(),
    );
    diag("tool", "generate_formula_batch_input", { count: queries.length, queries });
    const aggregateDeadlineAt = Date.now() + TOOL_CALL_TIMEOUT_MS;
    const acceptedQueries = queries.slice(0, TTF_MAX_CONCURRENT_QUERIES);
    const validationErrors = acceptedQueries.map(getFormulaQueryValidationError);
    const validQueries = acceptedQueries.filter(
      (_, index) => validationErrors[index] === null,
    );
    const validResults =
      validQueries.length > 0
        ? await resolveFormulasViaTtfRichBatch(
            validQueries,
            signal,
            TOOL_CALL_TIMEOUT_MS,
            aggregateDeadlineAt,
          )
        : [];
    let validResultIndex = 0;
    const results = queries.map((query, index) => {
      if (index >= TTF_MAX_CONCURRENT_QUERIES) {
        if (!query) {
          return { query, ...getFormulaQueriesRequiredError() };
        }
        return {
          query,
          error:
            `Batch exceeded the ${TTF_MAX_CONCURRENT_QUERIES}-query limit; ` +
            "resubmit this query in another generate_formula call.",
          retryable: true,
          retryWith: "queries" as const,
          maxQueries: TTF_MAX_CONCURRENT_QUERIES,
        };
      }
      const validationError = validationErrors[index];
      if (validationError) return { query, ...validationError };
      const result = validResults[validResultIndex];
      validResultIndex += 1;
      return annotateDataUnavailability(
        result ?? { query, error: "TTF returned no result for this query." },
      );
    });
    diag("tool", "generate_formula_batch_output", { results });
    const chainIds: string[] = [];
    const modelResults = results.map((entry) => {
      if ("error" in entry) return entry;
      const { chainId, ...rest } = entry;
      if (chainId) chainIds.push(chainId);
      return rest;
    });
    const unresolvedQueries = modelResults
      .filter(
        (entry) =>
          "error" in entry &&
          (entry as { dataUnavailable?: unknown }).dataUnavailable !== true,
      )
      .map((entry) => entry.query);
    const unavailableQueries = modelResults
      .filter(
        (entry) =>
          "error" in entry &&
          (entry as { dataUnavailable?: unknown }).dataUnavailable === true,
      )
      .map((entry) => entry.query);
    // Only entries the caller may resubmit belong in the continuation batches;
    // a definitively unavailable metric must not be queued for another round.
    const retryableQueries = modelResults
      .filter(
        (entry) =>
          "error" in entry &&
          (entry as { retryable?: unknown }).retryable !== false,
      )
      .map((entry) => entry.query);
    const resolvedCount = modelResults.length - unresolvedQueries.length;
    const status =
      unresolvedQueries.length === 0
        ? "complete"
        : resolvedCount > 0
          ? "partial"
          : "failed";
    return {
      output: JSON.stringify({
        ok: status === "complete",
        status,
        partial: status === "partial",
        retryable: retryableQueries.length > 0 && !signal?.aborted,
        results: modelResults,
        summary: {
          requestedCount: modelResults.length,
          resolvedCount,
          failedCount: unresolvedQueries.length,
          unavailableCount: unavailableQueries.length,
        },
        unresolvedQueries,
        unavailableQueries,
        continuationBatches: chunkQueries(
          retryableQueries,
          TTF_MAX_CONCURRENT_QUERIES,
        ),
      }),
      ...(chainIds.length > 0 ? { observationMetadata: { factsetChainIds: chainIds } } : {}),
    };
  }

  const query = typeof input?.query === "string" ? input.query.trim() : "";
  if (!query) {
    return {
      output: JSON.stringify({
        error: "Provide `query` (string) or `queries` (string[]).",
      }),
    };
  }
  const sizeError = getFormulaQuerySizeError(query);
  if (sizeError) {
    return { output: JSON.stringify(sizeError) };
  }
  diag("tool", "generate_formula_input", { query });
  const result = await resolveFormulaViaTtfRich(
    query,
    signal,
    TOOL_CALL_TIMEOUT_MS,
    Date.now() + TOOL_CALL_TIMEOUT_MS,
  );
  diag("tool", "generate_formula_output", { result });
  if ("error" in result) {
    return { output: JSON.stringify(annotateDataUnavailability(result)) };
  }
  const { chainId, ...modelResult } = result;
  return {
    output: JSON.stringify(modelResult),
    ...(chainId ? { observationMetadata: { factsetChainId: chainId } } : {}),
  };
}

/**
 * A definitive "FactSet has nothing for this" answer, as opposed to a timeout,
 * cancellation, or transport failure. Only the former justifies giving up on
 * the metric; the latter are worth another attempt.
 */
function isDataUnavailableError(error: string): boolean {
  return /returned no results/i.test(error);
}

/**
 * Tag a definitive no-data answer so the caller stops resubmitting it. Without
 * this the model cannot tell "FactSet does not carry this" apart from "the
 * request timed out", and retries a query that will never succeed.
 */
function annotateDataUnavailability<T extends object>(
  entry: T,
): T & { dataUnavailable?: true; retryable?: false } {
  const error = (entry as { error?: unknown }).error;
  if (typeof error !== "string" || !isDataUnavailableError(error)) {
    return entry;
  }
  return { ...entry, dataUnavailable: true, retryable: false };
}

function chunkQueries(queries: string[], size: number): string[][] {
  const chunks: string[][] = [];
  for (let start = 0; start < queries.length; start += size) {
    chunks.push(queries.slice(start, start + size));
  }
  return chunks;
}

